export type MediaType = "movie" | "tv";

export interface MediaItem {
  id: string;
  title: string;
  type: MediaType;
  year: number;
  region: string;
  director: string;
  actors: string[];
  rating: number;
  tags: string[];
  shortDesc: string;
  synopsis: string;
  poster: string;
  updatedAt: string;
  featured: boolean;
}

export const REGIONS = ["全部", "中国", "欧美", "日韩", "其他"];
export const YEARS = ["全部", "2025", "2024", "2023", "2022", "2021", "2020", "更早"];
export const SORTS = [
  { key: "default", label: "默认排序" },
  { key: "latest", label: "最新" },
  { key: "hot", label: "热门" },
] as const;

export type SortKey = (typeof SORTS)[number]["key"];

export const TYPE_LABEL: Record<MediaType, string> = {
  movie: "电影",
  tv: "电视剧",
};

export interface FilterOptions {
  region?: string;
  year?: string;
  sort?: SortKey;
}

export function filterMedia(items: MediaItem[], options: FilterOptions): MediaItem[] {
  const { region, year, sort = "default" } = options;
  let list = [...items];

  if (region && region !== "全部") {
    list = list.filter((item) => item.region === region);
  }

  if (year && year !== "全部") {
    if (year === "更早") {
      list = list.filter((item) => item.year < 2020);
    } else {
      list = list.filter((item) => item.year === Number(year));
    }
  }

  if (sort === "latest") {
    list.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  } else if (sort === "hot") {
    list.sort((a, b) => b.rating - a.rating);
  }

  return list;
}

export function searchMedia(items: MediaItem[], keyword: string): MediaItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return items.filter((item) => {
    const haystack = [item.title, item.director, ...item.actors, ...item.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRecommendations(item: MediaItem, items: MediaItem[], count = 6): MediaItem[] {
  const scored = items
    .filter((i) => i.id !== item.id)
    .map((i) => {
      let score = 0;
      if (i.type === item.type) score += 2;
      const sharedTags = i.tags.filter((t) => item.tags.includes(t)).length;
      score += sharedTags * 3;
      if (i.region === item.region) score += 1;
      return { item: i, score };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating);
  return scored.slice(0, count).map((s) => s.item);
}

export function getByType(items: MediaItem[], type: MediaType): MediaItem[] {
  return items.filter((item) => item.type === type);
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    totalPages,
    current,
    total: list.length,
  };
}

export const TYPE_NAMES: Record<string, string> = {
  movies: "电影",
  tv: "电视剧",
};